import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fristen } from '@/utils/api';

export interface FristDocument {
  id: number;
  original_file_name: string;
  file_size: number;
  mime_type: string;
  expires_at: string;
  requested_at: string | null;
  created_at: string;
  uploaded_by_name?: string;
  stageRank: number;
  color: 'green' | 'yellow' | 'orange' | 'red';
  daysLeft: number;
  snoozed: boolean;
  snoozeUntil: string | null;
}

export interface FristCategory {
  id: number;
  name: string;
  sort_order: number;
  document: FristDocument | null;
}

export interface FristGroup {
  group_name: string;
  categories: FristCategory[];
}

export interface FristHistoryEntry {
  id: number;
  original_file_name: string;
  expires_at: string;
  is_current: number;
  created_at: string;
  replaced_at: string | null;
  uploaded_by_name?: string;
}

export function useFristenCategories() {
  return useQuery<FristGroup[]>({
    queryKey: ['fristen', 'categories'],
    queryFn: () => fristen.getCategories().then((r) => r.data),
    placeholderData: (prev) => prev,
  });
}

export function useFristenStats() {
  return useQuery<{ warning_count: number; expired_count: number }>({
    queryKey: ['fristen', 'stats'],
    queryFn: () => fristen.getStats().then((r) => r.data),
    refetchInterval: 60000,
  });
}

export function useFristenHistory(categoryId: number | null) {
  return useQuery<FristHistoryEntry[]>({
    queryKey: ['fristen', 'history', categoryId],
    queryFn: () => fristen.getHistory(categoryId!).then((r) => r.data),
    enabled: categoryId !== null,
  });
}

export function useFristenSettings() {
  return useQuery<{ warn_recipients: string }>({
    queryKey: ['fristen', 'settings'],
    queryFn: () => fristen.getSettings().then((r) => r.data),
  });
}

function useInvalidateFristen() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['fristen'] });
}

export function useUploadFristDocument() {
  const invalidate = useInvalidateFristen();
  return useMutation({
    mutationFn: ({ categoryId, file, expiresAt }: { categoryId: number; file: File; expiresAt: string }) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('expires_at', expiresAt);
      return fristen.upload(categoryId, formData).then((r) => r.data);
    },
    onSuccess: invalidate,
  });
}

export function useCreateFristCategory() {
  const invalidate = useInvalidateFristen();
  return useMutation({
    mutationFn: (data: { group_name: string; name: string }) => fristen.createCategory(data).then((r) => r.data),
    onSuccess: invalidate,
  });
}

export function useRenameFristCategory() {
  const invalidate = useInvalidateFristen();
  return useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) => fristen.renameCategory(id, name).then((r) => r.data),
    onSuccess: invalidate,
  });
}

export function useDeleteFristCategory() {
  const invalidate = useInvalidateFristen();
  return useMutation({
    mutationFn: (id: number) => fristen.deleteCategory(id).then((r) => r.data),
    onSuccess: invalidate,
  });
}

export function useMarkFristRequested() {
  const invalidate = useInvalidateFristen();
  return useMutation({
    mutationFn: ({ documentId, clear }: { documentId: number; clear?: boolean }) =>
      fristen.markRequested(documentId, clear).then((r) => r.data),
    onSuccess: invalidate,
  });
}

export function useSendFristEmail() {
  return useMutation({
    mutationFn: (data: { document_ids: number[]; to: string; subject: string; message?: string }) =>
      fristen.sendEmail(data).then((r) => r.data),
  });
}

export function useUpdateFristenSettings() {
  const invalidate = useInvalidateFristen();
  return useMutation({
    mutationFn: (data: { warn_recipients: string }) => fristen.updateSettings(data).then((r) => r.data),
    onSuccess: invalidate,
  });
}
